import {
  Button,
  DrawerProps,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import StyledDrawer, { StyledDrawerTitle } from "Components/UI/StyledDrawer";
import {
  defaultChart,
  useReportBuilderContext,
} from "Providers/ReportBuilderProvider";
import { useCallback, useEffect } from "react";
import ChartBuilder from "./ChartBuilder";
import { IChart } from "Interfaces";
import { ChartBuilderSidebarBlock } from "./ChartBuilderSidebarBlock";
import { Delete, Save } from "@mui/icons-material";
import { friendlyDateTime } from "Helpers/dates";
import {
  deleteStandardChart,
  saveStandardChart,
} from "../services/chartService";
import { useNotification, useSelectedProject } from "Hooks";

interface IStandardChartEditorProps extends DrawerProps {
  chart: IChart;
  onSaved: () => void;
  onDeleted: () => void;
}

function StandardChartEditor({
  open,
  onClose,
  chart,
  onSaved,
  onDeleted,
}: IStandardChartEditorProps) {
  const { chart: editedChart, setChart } = useReportBuilderContext();
  const { selectedProject } = useSelectedProject();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  useEffect(() => {
    if (!open) {
      return;
    }
    setChart(chart);
  }, [open, setChart, chart]);

  const onDrawerClose = useCallback(
    (e) => {
      setChart(defaultChart);
      onClose?.(e, "backdropClick");
    },
    [onClose, setChart],
  );

  const onSave = useCallback(async () => {
    if (!selectedProject?.id || !chart.chartKey) return;
    try {
      await saveStandardChart(editedChart, selectedProject.id, chart.chartKey);
      onSaved();
      showSuccessNotification("Chart saved successfully");
    } catch (error) {
      showErrorNotification("Failed to save chart");
    }
  }, [
    chart,
    selectedProject?.id,
    showSuccessNotification,
    showErrorNotification,
    editedChart,
    onSaved,
  ]);

  const onDelete = useCallback(async () => {
    if (!selectedProject?.id || !chart.chartKey) return;
    try {
      await deleteStandardChart(selectedProject?.id, chart.chartKey);
      onDeleted();
      showSuccessNotification("Chart deleted successfully");
    } catch (error) {
      showErrorNotification("Failed to delete chart");
    }
  }, [
    selectedProject?.id,
    chart.chartKey,
    showSuccessNotification,
    showErrorNotification,
    onDeleted,
  ]);

  return (
    <StyledDrawer anchor="right" open={open} onClose={onDrawerClose}>
      <StyledDrawerTitle>
        <Typography variant="h6">Customize {chart.name}</Typography>
      </StyledDrawerTitle>
      <Stack
        sx={{
          height: `calc(100vh - 74px)`,
          width: "90vw",
        }}
      >
        <ChartBuilder
          sections={["yAxis", "displayOptions", "attributes"]}
          headerSlot={
            <ChartBuilderSidebarBlock>
              <Typography variant="h3">Customize your Chart</Typography>
              <Stack
                direction="row"
                justifyContent="space-between"
                spacing={1}
                alignItems="flex-end"
                sx={{ width: "100%" }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body1">
                    Last updated:{" "}
                    {chart?.updatedAt
                      ? friendlyDateTime(chart.updatedAt)
                      : "N/A"}
                  </Typography>
                  {chart.id && (
                    <Tooltip title="Delete chart">
                      <IconButton size="small" onClick={onDelete}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Save fontSize="small" />}
                  onClick={onSave}
                >
                  Save Changes
                </Button>
              </Stack>
            </ChartBuilderSidebarBlock>
          }
        />
      </Stack>
    </StyledDrawer>
  );
}

export default StandardChartEditor;
